import React, { useEffect, useState, type FC, type PropsWithChildren } from 'react';

import { Box, Container, Heading, Icon, ImageWithPlaceholder, Link, Text } from '@lucid/components';
import { getColor } from '@lucid/theme';
import { withImage } from '@lucid/hooks';
import { kebabCase } from 'lodash';

import type { Lucid } from 'types/types';

interface AgileHubCarouselProps {
  featuredAgilePages?: Lucid.Directus.Generated.Agile_Hub_Translations_Agile_Page[];
}

export type AgileColorTheme = {
  dark: Lucid.Theme.Color.ValueKeys;
  light: Lucid.Theme.Color.ValueKeys;
  active: Lucid.Theme.Color.ValueKeys | string;
};

export const getAgilePageColors = (colorTheme: Lucid.Theme.Color.Keys): AgileColorTheme => {
  switch (colorTheme) {
    case 'orange':
      return { light: 'orange.10', dark: 'orange.45', active: 'orange.100' };
    case 'teal':
      return { light: 'teal.10', dark: 'teal.40', active: 'teal.100' };
    case 'yellow':
      return { light: 'yellow.0', dark: 'yellow.20', active: '#644800' };
    case 'pink':
      return { light: 'pink.5', dark: 'pink.40', active: 'pink.100' };
    case 'blue':
      return { light: 'blue.10', dark: 'blue.50', active: 'blue.100' };
    default:
      return { light: 'coolGrey.10', dark: 'coolGrey.50', active: 'coolGrey.100' };
  }
};

const AgileHubCarousel: FC<PropsWithChildren<AgileHubCarouselProps>> = ({ featuredAgilePages }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSlideData, setCurrentSlideData] = useState<Lucid.Directus.Generated.Agile_Page_Translations>();
  const featuredAgilePagesId = featuredAgilePages?.map(
    (item: Lucid.Directus.Generated.Agile_Hub_Translations_Agile_Page) => item.agile_page_id,
  );

  useEffect(() => {
    setCurrentSlideData(featuredAgilePagesId?.[currentIndex]?.translations?.[0]);
  }, [currentIndex, featuredAgilePagesId]);

  return (
    <Container css={{ minHeight: '520px' }}>
      <Box
        css={theme => ({
          backgroundColor: theme.colors.coolGrey[100],
          padding: theme.space.s24,
          borderRadius: theme.radii.r24,
          display: 'grid',
          columnGap: theme.space.s24,
          marginBottom: theme.space.s80,
          color: theme.colors.white,
          gridTemplateColumns: '1fr',
          [theme.breakpoints.bp768.min]: {
            gridTemplateColumns: '40% 60%',
          },
          [theme.breakpoints.bp1024.min]: {
            gridTemplateColumns: '30% 70%',
          },
        })}
      >
        <Box>
          <ol
            css={theme => ({
              listStyleType: 'none',
              margin: 0,
              marginBottom: theme.space.s24,
              display: 'flex',
              flexDirection: 'column',
              rowGap: '12px',
              [theme.breakpoints.bp768.min]: {
                marginBottom: 0,
              },
            })}
          >
            {featuredAgilePagesId?.map((page, index: number) => {
              const translations = page?.translations?.[0];
              const isCurrentTab = currentIndex === index;

              return (
                <li key={page?.id || kebabCase(translations?.heading)}>
                  <Box
                    css={theme => ({
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      backgroundColor: isCurrentTab
                        ? getColor({
                            color: getAgilePageColors((page?.color_theme || '') as Lucid.Theme.Color.Keys).light,
                            theme,
                          })
                        : 'transparent',
                      border: isCurrentTab ? '2px solid' : 'none',
                      borderRadius: theme.radii.r12,
                      borderColor: getColor({
                        color: getAgilePageColors((page?.color_theme || '') as Lucid.Theme.Color.Keys).dark,
                        theme,
                      }),
                      '&:hover': {
                        border: '2px solid',
                        borderColor: getColor({
                          color: getAgilePageColors((page?.color_theme || '') as Lucid.Theme.Color.Keys).dark,
                          theme,
                        }),
                      },
                      '&:active': {
                        backgroundColor: getColor({
                          color: getAgilePageColors((page?.color_theme || '') as Lucid.Theme.Color.Keys).active,
                          theme,
                        }),
                      },
                      '&:hover p': {
                        padding: theme.space.s16,
                        paddingRight: 0,
                      },
                      '&:active p': {
                        color: theme.colors.white,
                      },
                    })}
                    onClick={() => setCurrentIndex(index)}
                  >
                    <Box css={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      <Text
                        css={theme => ({
                          padding: isCurrentTab ? theme.space.s16 : '18px',
                          paddingRight: 0,
                          marginBottom: 0,
                          color: isCurrentTab ? theme.colors.coolGrey[100] : theme.colors.white,
                        })}
                        variant={isCurrentTab ? 'labelS600' : 'labelS400'}
                      >
                        {`${index + 1}. ${translations?.agile_hub_short_title}`}
                      </Text>
                    </Box>
                    {isCurrentTab && (
                      <Box
                        css={theme => ({
                          borderRadius: theme.radii.r8,
                          backgroundColor: getColor({
                            color: getAgilePageColors((page?.color_theme || '') as Lucid.Theme.Color.Keys).dark,
                            theme,
                          }),
                          width: theme.space.s40,
                        })}
                      >
                        <Box
                          css={theme => ({
                            height: '100%',
                            padding: theme.space.s4,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          })}
                        >
                          <Icon css={theme => ({ height: theme.space.s16 })} type="CarotRight" />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </li>
              );
            })}
          </ol>
        </Box>
        <Box css={{ width: 'calc(100% - 24px)' }}>
          <ImageWithPlaceholder
            alt={currentSlideData?.heading_image?.translations?.[0]?.alt || ''}
            css={theme => ({ borderRadius: theme.radii.r16, height: 'auto' })}
            src={withImage(currentSlideData?.heading_image)}
          />
          <Heading as="h2" css={theme => ({ fontWeight: theme.fontWeights.fw500, marginBottom: 0 })}>
            {currentSlideData?.heading}
          </Heading>
          <Box
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginTop: theme.space.s16,
              marginBottom: theme.space.s32,
            })}
          >
            {currentSlideData?.agile_hub_details?.map(
              (detail: Lucid.Directus.Repeaters.Agile_Hub_Detail, index: number) => (
                <React.Fragment key={kebabCase(detail.agile_hub_detail)}>
                  <Text
                    css={theme => ({
                      color: theme.colors.coolGrey[30],
                      margin: 0,
                    })}
                    variant="labelS500"
                  >
                    {detail.agile_hub_detail}
                  </Text>
                  {index < currentSlideData.agile_hub_details.length - 1 && (
                    <Text css={theme => ({ margin: `0 ${theme.space.s8}px`, color: theme.colors.coolGrey[30] })}>
                      •
                    </Text>
                  )}
                </React.Fragment>
              ),
            )}
          </Box>
          <Text css={theme => ({ color: theme.colors.white })} variant="paragraphM400">
            {currentSlideData?.agile_hub_page_description}
          </Text>
          {currentSlideData?.path?.url && (
            <Link displayAsButton href={currentSlideData.path.url} iconAnimation="navigational" variant="secondary">
              Go to guide
            </Link>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default AgileHubCarousel;
